import {none, Opt} from 'ts-opt';

import {Accordion} from 'api/gen/Accordion';
import {AnimalList} from 'api/gen/AnimalList';
import {AvailableAnimal} from 'api/gen/AvailableAnimal';
import {Bookmark} from 'api/gen/Bookmark';
import {BreederToChoose} from 'api/gen/BreederToChoose';
import {CreateOrUpdateGridUserSettings} from 'api/gen/CreateOrUpdateGridUserSettings';
import {CriteriaDefinition} from 'api/gen/CriteriaDefinition';
import {CriteriaList} from 'api/gen/CriteriaList';
import {FarmToChoose} from 'api/gen/FarmToChoose';
import {MatingResult} from 'api/gen/MatingResult';
import {OrganizationToChoose} from 'api/gen/OrganizationToChoose';
import {PagedModelFemaleHolsteinAnimalInList} from 'api/gen/PagedModelFemaleHolsteinAnimalInList';
import {PagedModelMaleFleckviehAnimalInList} from 'api/gen/PagedModelMaleFleckviehAnimalInList';
import {PagedModelMaleHolsteinAnimalInList} from 'api/gen/PagedModelMaleHolsteinAnimalInList';
import {ScoringResult} from 'api/gen/ScoringResult';
import {Sex} from 'api/gen/Sex';
import {WriteCriteria} from 'api/gen/WriteCriteria';

import {BullPreference} from '../types/bull-counts-and-shares';
import {MatingLoadingId} from '../types/mating-loading-id';
import {MatingTabKey} from '../types/mating-tab-key';

/** Mating session phase for a single tab. */
export type SessionState = 'initial' | 'filter' | 'selection';

/** Complete mating slice state. */
export interface MatingState {
    /** Currently selected mating tab. */
    activeTab: MatingTabKey;
    /** Loading flags for the active workflow. */
    loading: Array<MatingLoadingId>;
    /** Saved breeders available in the female workflow. */
    breeders: Opt<Array<BreederToChoose>>;
    /** Saved farms available in the female workflow. */
    farms: Opt<Array<FarmToChoose>>;
    /** Loaded page of female animals. */
    femaleAnimalsPage: Opt<PagedModelFemaleHolsteinAnimalInList>;
    /** Whether the female session already contains animals. */
    cowsExistInSession: boolean;
    /** Current female session phase. */
    cowsSessionState: SessionState;
    /** Loaded page of Fleckvieh bulls. */
    maleFleckviehAnimalsPage: Opt<PagedModelMaleFleckviehAnimalInList>;
    /** Loaded page of Holstein bulls. */
    maleHolsteinAnimalsPage: Opt<PagedModelMaleHolsteinAnimalInList>;
    /** Whether the male session already contains animals. */
    bullsExistInSession: boolean;
    /** Current male session phase. */
    bullsSessionState: SessionState;
    /** Modal workflow marker for animal lists. */
    animalListsModalData: Sex | null;
    /** Modal workflow marker for animal-list confirmation. */
    animalListsConfirmModalData: Sex | null;
    /** Currently selected animal list id. */
    animalListId: Opt<number>;
    /** Animals available for the add-animals modal. */
    availableAnimals: Opt<Array<AvailableAnimal>>;
    /** Modal workflow marker for add-animals dialogs. */
    addAnimalsModalData: Sex | null;
    /** Saved animal lists for the current workflow. */
    animalLists: Opt<Array<AnimalList>>;
    /** Bookmark that identifies the current animal-list tab. */
    animalListsTag: Opt<Sex>;
    /** Modal workflow marker for add-from-DB dialogs. */
    animalsFromDBVisibilityModalData: Sex | null;
    /** Modal workflow marker for the saved-lists overview. */
    listsOverviewModalData: Bookmark | null;
    /** Saved criteria lists for the current workflow. */
    criteriaLists: Opt<Array<CriteriaList>>;
    /** Bookmark that identifies the current criteria-list tab. */
    criteriaListsTag: Opt<Bookmark>;
    /** Accordion groups available in the criteria form. */
    criteriaAccordions: Opt<Array<Accordion>>;
    /** Criteria definitions for the cow workflow. */
    cowCriteriaDefinitions: Opt<Array<CriteriaDefinition>>;
    /** Criteria definitions for the bull workflow. */
    bullCriteriaDefinitions: Opt<Array<CriteriaDefinition>>;
    /** Criteria definitions for the params workflow. */
    paramsCriteriaDefinitions: Opt<Array<CriteriaDefinition>>;
    /** Minimum female age used by the workflow. */
    minimumFemaleAgeInMonths: Opt<number>;
    /** Criteria values written back to the backend. */
    writeCriteria: Opt<Array<WriteCriteria>>;
    /** Currently selected criteria list id. */
    criteriaListId: Opt<number>;
    /** Modal workflow marker for criteria lists. */
    criteriaListsModalData: Bookmark | null;
    /** Modal workflow marker for criteria-list confirmation. */
    criteriaListsConfirmModalData: Bookmark | null;
    /** Modal workflow marker for criteria-list details. */
    criteriaListDetailsModalData: Bookmark | null;
    /** Whether the save-scoring-results modal is visible. */
    saveScoringResultsModalVisible: boolean;
    /** Saved organizations available in the bull workflow. */
    organizations: Opt<Array<OrganizationToChoose>>;
    /** Latest mating score returned by the backend. */
    matingResult: Opt<MatingResult>;
    /** Bull preference counts for the result tab. */
    bullPreferences: Opt<Array<BullPreference>>;
    /** Adjusted scoring results shown in the result tab. */
    recalculatedScoringResults: Opt<Array<ScoringResult>>;
    /** Whether the export-results modal is visible. */
    exportBullselectorResultModalVisible: boolean;
    /** Whether the reset-all-settings flow is active. */
    toggleResetScreen: boolean;
    /** Saved grid-user settings for the current workflow. */
    gridUserSettings: Opt<CreateOrUpdateGridUserSettings>;
}

/** Initial state for the mating slice. */
export const initialMatingState: MatingState = {
    activeTab: 'cowSelection',
    loading: [],
    breeders: none,
    farms: none,
    femaleAnimalsPage: none,
    cowsExistInSession: false,
    cowsSessionState: 'initial',
    maleFleckviehAnimalsPage: none,
    maleHolsteinAnimalsPage: none,
    bullsExistInSession: false,
    bullsSessionState: 'initial',
    animalListsModalData: null,
    animalListsConfirmModalData: null,
    animalListId: none,
    availableAnimals: none,
    addAnimalsModalData: null,
    animalLists: none,
    animalListsTag: none,
    animalsFromDBVisibilityModalData: null,
    listsOverviewModalData: null,
    criteriaLists: none,
    criteriaListsTag: none,
    criteriaAccordions: none,
    cowCriteriaDefinitions: none,
    bullCriteriaDefinitions: none,
    paramsCriteriaDefinitions: none,
    minimumFemaleAgeInMonths: none,
    writeCriteria: none,
    criteriaListId: none,
    criteriaListsModalData: null,
    criteriaListsConfirmModalData: null,
    criteriaListDetailsModalData: null,
    saveScoringResultsModalVisible: false,
    organizations: none,
    matingResult: none,
    bullPreferences: none,
    recalculatedScoringResults: none,
    exportBullselectorResultModalVisible: false,
    toggleResetScreen: false,
    gridUserSettings: none,
};
